import { cx } from '@linaria/core';
import { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2SitemapStore, findPageDescriptorById } from '@pm2/sitemap-utils';
import { pm2InterfaceStore } from '@pm2/store';
import { useNavigationManager } from '@provider/navigation';
import { breakpoints, resets } from '@ui/css';
import { FocusOn } from '@ui/kit';
import { CrossIcon, ArrowIcon } from '@ui/kit/icons';
import { throttleToAnimationFrame } from '@utils/common';
import { useMatchBreakpoint, IsomorphicPortal, CSSRefTransition, assertRefObject, useForceHeaderExpanded, SizeTransition } from '@utils/react';
import { MegaMenuFilters } from './atom.filters.js';
import { MegaMenuLayerDisplayTypes, MegaMenuLayer } from './atom.layer.js';
import { LAYER_FILTER_TRANSITION_DURATION_MS, layerTransitionCls as layerTransitionCls$1 } from './atom.layer.style.js';
import { MegaMenuLeftNav } from './atom.left-nav.js';
import { TRANSITION_DURATION_MS, dropdownTransitionCls } from './dropdown-transition.style.js';
import { useFilters } from './hooks/useFilters.js';
import { useOnNavigate } from './hooks/useOnNavigate.js';
import { CONTENT_TRANSITION_DURATION_MS, CONTENT_RENDER_DELAY_MS, arrowCls, mobileContentTransitionCls, contentTransitionCls, closeButtonCls, goBackButtonCls, headingCls, layerTransitionCls, layerWrapperCls, contentCls, wrapperCls, backdropCls, megaMenuCls } from './root.wrapper.style.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function MegaMenuWrapper() {
  const navigation = useNavigationManager();
  const isMobile = useMatchBreakpoint(breakpoints.mobile) ?? false;
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const megaMenu = pm2InterfaceStore.useSelector(state => state.megaMenu)?.map(id => findPageDescriptorById(sitemapState, id)).filter(it => it !== undefined);

  // Ensure megaMenu is auto-closed upon navigation
  useEffect(() => {
    return navigation.subscribe(() => {
      pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.closeMegaMenu());
    });
  }, [navigation]);

  // NB: We MUST to pull out the following props from the MegaMenuContent
  // component in order to ensure that previous content is properly retained by
  // <TransitionGroup> once the component opens/closes.
  const rootMegaMenuDescriptor = megaMenu?.[0];
  const currentDescriptor = megaMenu?.[megaMenu.length - 1];
  const selectedLeftNavDescriptor = (megaMenu?.length ?? 0) > 1 ? megaMenu?.[1] : undefined;
  const renderAsTwoColumns = shouldRenderAsTwoColumns(megaMenu, isMobile);

  // transition-key for transitioning MegaMenuLayer, Filter and goBack-button,
  // which is only applicable when transitioning inside twoColumn mode.
  const twoColumnKey = renderAsTwoColumns && (megaMenu?.length ?? 0) <= 2 ? (megaMenu?.map(it => it.id) ?? []).join("-") : undefined;

  // default transition-key to be used when navigating outside of two-column
  // mode, which transitions all content (MegaMenuLayer, filters, go-back button
  // and leftNav).
  const contentKey = twoColumnKey ? undefined : (megaMenu?.map(it => it.id) ?? []).join("-");

  // Extract all available filters based on the current layer descriptors.
  const filterResults = useFilters(currentDescriptor, currentDescriptor?.children ?? []);
  const filterKey = generateFilterKey(currentDescriptor, filterResults.matchingPageDescriptors);
  const currentDisplayType = getCurrentDisplayType(megaMenu);
  const layerDisplayType = getLayerDisplayType(currentDisplayType, filterResults.matchingPageDescriptors);
  return /*#__PURE__*/jsx(IsomorphicPortal, {
    children: /*#__PURE__*/jsx(TransitionGroup, {
      component: null,
      children: !!rootMegaMenuDescriptor && currentDescriptor && /*#__PURE__*/jsx(CSSRefTransition, {
        classNames: dropdownTransitionCls,
        timeout: TRANSITION_DURATION_MS,
        children: ref => /*#__PURE__*/jsx(MegaMenuContent, {
          ref: ref,
          rootMegaMenuDescriptor: rootMegaMenuDescriptor,
          currentDescriptor: currentDescriptor,
          selectedLeftNavDescriptor: selectedLeftNavDescriptor,
          renderAsTwoColumns: renderAsTwoColumns,
          contentKey: contentKey,
          twoColumnKey: twoColumnKey,
          filterKey: filterKey,
          filterResults: filterResults,
          layerDisplayType: layerDisplayType
        })
      }, rootMegaMenuDescriptor.id)
    })
  });
}
/**
 * The content of the megaMenu is responsible for handling ALL content inside
 * a megamenu. This includes content itself, filters and left navigation.
 *
 * Each of the three types of content needs to be able to trigger transitions
 * independently of each other, depending on different keys. These transitions
 * also needs to target different parts of the content. As such three distinct
 * TransitionGroups are used within the component.
 */
function MegaMenuContent({
  rootMegaMenuDescriptor,
  currentDescriptor,
  selectedLeftNavDescriptor,
  renderAsTwoColumns,
  contentKey,
  twoColumnKey,
  filterKey,
  filterResults: {
    matchingPageDescriptors,
    gradeFilters,
    categoryFilters,
    selectedCategoryFilters,
    selectedGradeFilters,
    setCategoryFilters,
    setGradeFilters
  },
  layerDisplayType,
  ref: externalRef
}) {
  const isMobile = useMatchBreakpoint(breakpoints.mobile) ?? false;
  const [arrowOffset, setArrowOffset] = useState(undefined);
  const reverseTransition = useRef(false);
  const interalWrapperRef = useRef(null);
  const wrapperRef = externalRef ?? interalWrapperRef;
  assertRefObject(wrapperRef);

  // In desktop mode the header should always be expanded when a megamenu is
  // active.
  useForceHeaderExpanded(!isMobile);
  const closeMegaMenu = useCallback(() => {
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.closeMegaMenu());
  }, []);
  const showGoBackButton = shouldShowGoBackButton(rootMegaMenuDescriptor, currentDescriptor, renderAsTwoColumns);
  const onNavigate = useOnNavigate(closeMegaMenu);

  /**
   * Goes back one level, by removing the most resent navigation-layer of the
   * mega-menu.
   */
  const goBack = () => {
    reverseTransition.current = true;
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.popMegaMenu());
    setTimeout(() => {
      reverseTransition.current = false;
    }, CONTENT_TRANSITION_DURATION_MS);
  };
  useEffect(() => {
    wrapperRef.current?.classList.add("height-transition-active");
    setTimeout(() => {
      wrapperRef.current?.classList.remove("height-transition-active");
    }, CONTENT_TRANSITION_DURATION_MS + CONTENT_RENDER_DELAY_MS + 30);
  }, [wrapperRef, filterKey, contentKey, twoColumnKey]);
  useLayoutEffect(() => {
    const updateOffset = () => {
      const wrapper = wrapperRef.current;
      const activeMenuItem = document.querySelector("[data-nav-button].active");
      if (!wrapper || !activeMenuItem) {
        return;
      }
      setArrowOffset(`${activeMenuItem.getBoundingClientRect().left - wrapper.getBoundingClientRect().left + activeMenuItem.clientWidth / 2 - 10}px`);
    };
    updateOffset();
    const handleResize = throttleToAnimationFrame(updateOffset);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [wrapperRef]);
  return /*#__PURE__*/jsx("section", {
    ref: wrapperRef,
    className: cx(resets.rootCls, megaMenuCls, renderAsTwoColumns && "withLeftNav"),
    children: /*#__PURE__*/jsxs(FocusOn, {
      onEscapeKey: closeMegaMenu,
      onClickOutside: closeMegaMenu,
      scrollLock: true,
      focusLock: true,
      allowPinchZoom: true,
      autoFocus: true,
      returnFocus: {
        preventScroll: true
      },
      preventScrollOnFocus: true,
      children: [/*#__PURE__*/jsxs("div", {
        "data-is-wrapper": true,
        className: cx(wrapperCls, renderAsTwoColumns && "withLeftNav", reverseTransition.current && "reverse"),
        children: [/*#__PURE__*/jsx("span", {
          className: arrowCls,
          style: {
            left: arrowOffset
          }
        }), /*#__PURE__*/jsx(TransitionGroup, {
          component: null,
          children: /*#__PURE__*/jsx(SizeTransition, {
            axis: "vertical",
            swappable: true,
            timeout: CONTENT_TRANSITION_DURATION_MS,
            delayMs: CONTENT_RENDER_DELAY_MS,
            classNames: isMobile ? mobileContentTransitionCls : contentTransitionCls,
            children: ref => /*#__PURE__*/jsxs("div", {
              ref: ref,
              className: cx(contentCls, isMobile ? mobileContentTransitionCls : contentTransitionCls, renderAsTwoColumns && "withLeftNav"),
              children: [/*#__PURE__*/jsx("button", {
                className: closeButtonCls,
                "aria-label": "Luk menuen",
                onClick: closeMegaMenu,
                children: /*#__PURE__*/jsx(CrossIcon, {})
              }), renderAsTwoColumns && /*#__PURE__*/jsx(MegaMenuLeftNav, {
                rootDescriptor: rootMegaMenuDescriptor,
                selectedDescriptor: selectedLeftNavDescriptor,
                onNavigate: onNavigate
              }), /*#__PURE__*/jsx("div", {
                className: cx(layerWrapperCls, renderAsTwoColumns && "withLeftNav"),
                children: /*#__PURE__*/jsx(TransitionGroup, {
                  component: "div",
                  children: /*#__PURE__*/jsx(SizeTransition, {
                    axis: "vertical",
                    swappable: true,
                    classNames: isMobile ? mobileContentTransitionCls : contentTransitionCls,
                    delayMs: CONTENT_RENDER_DELAY_MS,
                    timeout: CONTENT_TRANSITION_DURATION_MS,
                    children: ref => /*#__PURE__*/jsxs("div", {
                      ref: ref,
                      className: cx(isMobile ? mobileContentTransitionCls : contentTransitionCls),
                      children: [showGoBackButton ? /*#__PURE__*/jsxs("button", {
                        className: goBackButtonCls,
                        onClick: goBack,
                        children: [/*#__PURE__*/jsx(ArrowIcon, {}), /*#__PURE__*/jsx("span", {
                          className: "text",
                          children: currentDescriptor.title
                        })]
                      }) : /*#__PURE__*/jsx("span", {
                        className: headingCls,
                        children: currentDescriptor.title
                      }), (categoryFilters || gradeFilters) && /*#__PURE__*/jsx(MegaMenuFilters, {
                        categoryFilters: categoryFilters,
                        gradeFilters: gradeFilters,
                        selectedCategories: selectedCategoryFilters,
                        selectedGrades: selectedGradeFilters,
                        setCategoryFilters: setCategoryFilters,
                        setGradeFilters: setGradeFilters
                      }), /*#__PURE__*/jsx(TransitionGroup, {
                        component: "div",
                        className: layerTransitionCls,
                        children: /*#__PURE__*/jsx(SizeTransition, {
                          axis: "vertical",
                          swappable: true,
                          classNames: layerTransitionCls$1,
                          delayMs: CONTENT_RENDER_DELAY_MS,
                          timeout: LAYER_FILTER_TRANSITION_DURATION_MS,
                          children: ref => /*#__PURE__*/jsx("div", {
                            ref: ref,
                            className: layerTransitionCls$1,
                            children: /*#__PURE__*/jsx(MegaMenuLayer, {
                              pageDescriptors: matchingPageDescriptors,
                              displayType: layerDisplayType,
                              onNavigate: onNavigate
                            })
                          })
                        }, filterKey)
                      })]
                    })
                  }, twoColumnKey)
                })
              })]
            })
          }, contentKey)
        })]
      }), /*#__PURE__*/jsx("span", {
        className: backdropCls,
        onClick: closeMegaMenu,
        role: "none"
      })]
    })
  });
}

/**
 * Extract the currently desired displayType.. As soon as we're more than one
 * level down, then use the levelTwo configuration.
 */
function getCurrentDisplayType(megaMenu) {
  return (megaMenu?.length === 1 ? megaMenu?.[0]?.megaMenu.displayType.levelOne : megaMenu?.[0]?.megaMenu.displayType.levelTwo) ?? "text";
}

/**
 * We may ONLY render the MegaMenu layer with vignettes in case the current
 * displayType is set so and if all layerDescriptors have vignettes associated
 */
function getLayerDisplayType(currentDisplayType, layerDescriptors) {
  return currentDisplayType === "vignettes" && layerDescriptors?.every(descriptor => !!descriptor.vignette) ? MegaMenuLayerDisplayTypes.VIGNETTE : MegaMenuLayerDisplayTypes.TEXT;
}

/**
 * Once we have navigated into the mega-menu structure, then display the goBack
 * button (however, not while leftNav is rendered, since this will handle
 * navigtion between descriptors).
 */
function shouldShowGoBackButton(rootDescriptor, currentDescriptor, renderAsTwoColumns) {
  return renderAsTwoColumns ? false : rootDescriptor.id !== currentDescriptor.id;
}

/**
 * In order to render in two_column mode then it must be specified in the
 * configuration, and we must be in level one or two as per spec.
 */
function shouldRenderAsTwoColumns(megaMenu, isMobile) {
  return isMobile ? false : (megaMenu?.length ?? 0) <= 2 && megaMenu?.[0]?.megaMenu.displayType.levelOne === "twoColumns";
}

/**
 * Generate a unique display key based on the currently visible set of
 * page descriptors, so that we do not needlessly generate transition
 * animations
 */
function generateFilterKey(menuRootDescriptor, pageDescriptors) {
  return `${menuRootDescriptor?.id}-${pageDescriptors.map(it => it.id).join()}`;
}

export { MegaMenuWrapper };
//# sourceMappingURL=root.wrapper.js.map
